import axios from "axios";
import api from "../services/api";

// Two-phase upload: presign with our API, then PUT bytes directly to S3.
// This bypasses ALB/WAF/PHP-FPM for the file body, which was silently
// dropping large multipart POSTs on some user networks.
export async function uploadFilesViaS3(
  presignPath,
  files,
  extraPresignBody = {},
  onProgress
) {
  const fileSpecs = files.map((f) => ({
    name: f.name,
    size: f.size,
    mime: f.type || "application/octet-stream",
  }));

  const { data: presign } = await api.post(presignPath, {
    ...extraPresignBody,
    files: fileSpecs,
  });

  if (!presign?.success || !Array.isArray(presign.data?.files)) {
    throw new Error(presign?.message || "Failed to obtain upload URLs");
  }
  const slots = presign.data.files;
  if (slots.length !== files.length) {
    throw new Error("Presign returned unexpected number of upload URLs");
  }

  const loaded = new Array(files.length).fill(0);
  const total = files.reduce((s, f) => s + f.size, 0);
  const report = () => {
    if (onProgress) onProgress(loaded.reduce((s, n) => s + n, 0), total);
  };

  await Promise.all(
    slots.map((slot, i) =>
      putWithRetry(slot, files[i], fileSpecs[i].mime, (l) => {
        loaded[i] = l;
        report();
      })
    )
  );

  return {
    upload_id: presign.data.upload_id,
    files: slots.map((slot, i) => ({
      key: slot.key,
      name: files[i].name,
      size: files[i].size,
      mime: fileSpecs[i].mime,
    })),
  };
}

async function putWithRetry(slot, file, mime, onLoaded, retries = 2) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      onLoaded(0);
      return await axios.put(slot.url, file, {
        headers: slot.headers || { "Content-Type": mime },
        // Prevent axios from JSON-serializing the File/Blob payload.
        transformRequest: [(d) => d],
        timeout: 300000,
        onUploadProgress: (evt) => onLoaded(evt.loaded),
      });
    } catch (err) {
      lastErr = err;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
  }
  throw lastErr;
}
